import { useSelector } from 'react-redux';
import State from 'store/State';
import Order from 'communication/types/order/Order';
import { createSelector } from 'reselect';

export const selectOrdersState = (state: State) => state.app.orders;

export const useOrdersStateSelector = () => useSelector(selectOrdersState);

export const useSelectFirstBatchComplete = () =>
    useOrdersStateSelector().firstBatchComplete;

export const selectStreamKey = (state: State) => {
    return selectOrdersState(state).streamKey;
};

export const selectOrdersByOrderIndexes: (
    orders: Order[],
    orderIndexes: number[]
) => Order[] = (orders, orderIndexes) => {
    return orderIndexes
        .map((i) => orders[i])
        .filter(
            // Ensure that we only return orders for indexes that are in the orders stream...
            (o) => !!o
        );
};

export const selectOrders: (state: State, orderIndexes: number[]) => Order[] = (
    state,
    orderIndexes
) => {
    const orders = selectOrdersState(state);
    return selectOrdersByOrderIndexes(orders.data || [], orderIndexes);
};

export const selectOrdersData = (state: State) => selectOrdersState(state).data;

export const selectAllOrders: (state: State) => Order[] = (state) => {
    const orders = selectOrdersState(state);
    const data = orders.data || [];
    return data.filter((d) => !!d);
};

export const selectOrdersStreamingColumns = (state: State) =>
    selectOrdersState(state).streamingColumns;

export const selectOrdersStreamingOrderIds = (state: State) =>
    selectOrdersState(state).streamingOrderIds;

/*
 * Selects the streaming orders
 * Note: if any of the streamingOrderIds are not present in the orders data, this means
 * the orders request is in flight and we don't have them in the order stream yet - so we return an empty array
 */
const selectStreamingOrdersEmptyResult: Order[] = [];

export const selectStreamingOrders = createSelector(
    [selectOrdersData, selectOrdersStreamingOrderIds],
    (orders, streamingOrderIds): Order[] => {
        if (!orders || streamingOrderIds.length === 0) {
            return selectStreamingOrdersEmptyResult;
        }
        const ret = [];
        for (const orderId of streamingOrderIds) {
            const order = orders.find((o) => o && o.orderID === orderId);
            if (!order) {
                return selectStreamingOrdersEmptyResult;
            }
            ret.push(order);
        }
        return ret;
    }
);
